import api from "../../services/axiosInterceptor.js";

/**
 * Build axios path for a gcsAsset pointer (matches GetData `api/...` convention).
 */
export function buildApiAssetPath(gcsBucket, gcsObject) {
    const bucket = String(gcsBucket || "").trim();
    const objectKey = String(gcsObject || "").replace(/^\/+/, "").trim();
    if (!bucket || !objectKey) return "";
    return `api/broylesllc/assets/${encodeURIComponent(bucket)}/${objectKey
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`;
}

/**
 * Prefer gcsBucket/gcsObject pointers; fall back to migration sourceUrl.
 * @returns {string} cache key / fetch target (api path or absolute URL)
 */
export function assetFetchKey(assetOrUrl) {
    if (assetOrUrl == null) return "";
    if (typeof assetOrUrl === "string") {
        const raw = assetOrUrl.trim();
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        const path = raw.replace(/^\//, "");
        return path.startsWith("api/") ? path : `api/${path}`;
    }
    const fromPointer = buildApiAssetPath(assetOrUrl.gcsBucket, assetOrUrl.gcsObject);
    if (fromPointer) return fromPointer;
    return assetFetchKey(assetOrUrl.sourceUrl || "");
}

export function toFetchTarget(keyOrUrl) {
    const raw = String(keyOrUrl || "").trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return { kind: "absolute", url: raw };
    const path = raw.replace(/^\//, "");
    const apiPath = path.startsWith("api/") ? path : `api/${path}`;
    return { kind: "api", url: apiPath };
}

const MAX_CACHE_ENTRIES = 48;
const MAX_CACHE_BYTES = 48 * 1024 * 1024;

/** @type {Map<string, { blobUrl: string, contentType: string, sizeBytes: number, owned: boolean, refCount: number }>} */
const assetCache = new Map();

/** @type {Map<string, Promise<{ blobUrl: string, contentType: string, release: () => void }>>} */
const inflight = new Map();

let cachedBytes = 0;

function touch(key, entry) {
    assetCache.delete(key);
    assetCache.set(key, entry);
}

function evictOne() {
    for (const [key, entry] of assetCache) {
        if (entry.refCount > 0) continue;
        assetCache.delete(key);
        if (entry.owned && entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
        cachedBytes = Math.max(0, cachedBytes - (entry.sizeBytes || 0));
        return true;
    }
    return false;
}

function evictUntilFit(nextBytes = 0) {
    while (
        assetCache.size > 0 &&
        (assetCache.size >= MAX_CACHE_ENTRIES ||
            cachedBytes + nextBytes > MAX_CACHE_BYTES)
    ) {
        if (!evictOne()) break; // all remaining entries are in use
    }
}

function remember(key, entry) {
    evictUntilFit(entry.sizeBytes || 0);
    touch(key, entry);
    cachedBytes += entry.sizeBytes || 0;
}

function releaseKey(key) {
    const entry = assetCache.get(key);
    if (!entry) return;
    entry.refCount = Math.max(0, (entry.refCount || 0) - 1);
    // Opportunistic trim when nothing holds the entry
    if (entry.refCount === 0) {
        evictUntilFit(0);
    }
}

/**
 * Acquire a blob URL for an asset pointer or legacy URL.
 * Call `release()` when the consumer unmounts (refcount).
 */
export async function acquireAssetObjectUrl(assetOrUrl, contentTypeHint = "") {
    const key = assetFetchKey(assetOrUrl);
    const target = toFetchTarget(key);
    if (!target) {
        throw new Error("No file URL.");
    }

    const hit = assetCache.get(key);
    if (hit) {
        hit.refCount += 1;
        touch(key, hit);
        return {
            blobUrl: hit.blobUrl,
            contentType: hit.contentType || contentTypeHint || "",
            release: () => releaseKey(key),
        };
    }

    const pending = inflight.get(key);
    if (pending) {
        const shared = await pending;
        const entry = assetCache.get(key);
        if (entry) entry.refCount += 1;
        return {
            blobUrl: shared.blobUrl,
            contentType: shared.contentType,
            release: () => releaseKey(key),
        };
    }

    const promise = (async () => {
        if (target.kind === "absolute") {
            const entry = {
                blobUrl: target.url,
                contentType: contentTypeHint || "",
                sizeBytes: 0,
                owned: false,
                refCount: 0,
            };
            remember(key, entry);
            return {
                blobUrl: entry.blobUrl,
                contentType: entry.contentType,
            };
        }

        const res = await api.get(target.url, { responseType: "blob" });
        const contentType =
            contentTypeHint ||
            res.headers?.["content-type"] ||
            res.data?.type ||
            "application/octet-stream";
        const sizeBytes =
            typeof res.data?.size === "number" && Number.isFinite(res.data.size)
                ? res.data.size
                : 0;
        const blobUrl = URL.createObjectURL(res.data);
        const entry = {
            blobUrl,
            contentType,
            sizeBytes,
            owned: true,
            refCount: 0,
        };
        remember(key, entry);
        return { blobUrl: entry.blobUrl, contentType: entry.contentType };
    })();

    inflight.set(key, promise);
    try {
        const loaded = await promise;
        const entry = assetCache.get(key);
        if (entry) entry.refCount += 1;
        return {
            blobUrl: loaded.blobUrl,
            contentType: loaded.contentType,
            release: () => releaseKey(key),
        };
    } finally {
        inflight.delete(key);
    }
}

/** @deprecated Use acquireAssetObjectUrl — kept for call sites mid-migration. */
export async function fetchAssetObjectUrl(assetOrUrl, contentTypeHint = "") {
    return acquireAssetObjectUrl(assetOrUrl, contentTypeHint);
}

export function getAssetCacheStats() {
    let pinned = 0;
    for (const entry of assetCache.values()) {
        if (entry.refCount > 0) pinned += 1;
    }
    return {
        entries: assetCache.size,
        pinned,
        cachedBytes,
        maxEntries: MAX_CACHE_ENTRIES,
        maxBytes: MAX_CACHE_BYTES,
    };
}
